import { useState } from 'react';
import { genId, validateUrl, postApi } from './ProxyUtils';
import { useInlineEdit } from './useInlineEdit';

// Factory che genera handlers CRUD per una lista globale (whitelist o blacklist)
const createGlobalHandlers = ({
    listSetter, editHook, newUrlState, setStatus, setIsSubmitting, envKey, listName, idPrefix
}) => {
    const handleEdit = (id, currentUrl) => editHook.start(id, currentUrl);
    const handleCancel = () => editHook.cancel();

    const handleSaveEdit = async (id) => {
        const trimmed = editHook.editValue.trim();
        if (!trimmed) {
            setStatus({ type: 'error', message: "L'URL non può essere vuoto" });
            return;
        }

        let duplicate = false;
        listSetter(prev => {
            duplicate = prev.some(item => item.url === trimmed && item.id !== id);
            return prev;
        });
        if (duplicate) {
            setStatus({ type: 'error', message: 'URL già presente' });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = { id, url: trimmed, type: 'url', action: 'update' };

            await postApi(envKey, payload, {
                mock: async () => {
                    await new Promise(r => setTimeout(r, 500));
                    return { statusRequest: 'success' };
                }
            });

            listSetter(prev => prev.map(item =>
                item.id === id ? { ...item, url: trimmed } : item
            ));
            editHook.cancel();
            setStatus({ type: 'success', message: `URL ${listName} globale modificato con successo` });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: err.message || 'Errore di connessione' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id, itemUrl) => {
        setIsSubmitting(true);
        try {
            const payload = { id, url: itemUrl.trim(), action: 'delete' };

            await postApi(envKey, payload, {
                mock: async () => {
                    await new Promise(r => setTimeout(r, 500));
                    return { statusRequest: 'success' };
                }
            });

            listSetter(prev => prev.filter(item => item.id !== id));
            setStatus({ type: 'success', message: `Elemento rimosso dalla ${listName} globale` });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: err.message || 'Errore di connessione' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAdd = async () => {
        const trimmed = newUrlState.value.trim();
        if (!trimmed) {
            setStatus({ type: 'error', message: "L'URL non può essere vuoto" });
            return;
        }
        if (!validateUrl(trimmed)) {
            setStatus({ type: 'error', message: 'URL non valido' });
            return;
        }

        let duplicate = false;
        listSetter(prev => {
            duplicate = prev.some(item => item.url === trimmed);
            return prev;
        });
        if (duplicate) {
            setStatus({ type: 'error', message: `URL già presente nella ${listName} globale` });
            return;
        }

        setIsSubmitting(true);
        try {
            const payload = { url: trimmed, type: 'url', action: 'add' };

            const data = await postApi(envKey, payload, {
                mock: async () => {
                    await new Promise(r => setTimeout(r, 500));
                    return { statusRequest: 'success', data: { id: genId(idPrefix) } };
                }
            });

            const newItem = {
                id: data?.data?.id || genId(idPrefix),
                url: trimmed,
                type: 'url'
            };
            listSetter(prev => [...prev, newItem]);
            newUrlState.set('');
            setStatus({ type: 'success', message: `Elemento aggiunto alla ${listName} globale` });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: err.message || 'Errore di connessione' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return { handleEdit, handleSaveEdit, handleCancel, handleDelete, handleAdd };
};

export const useGlobalListLogic = (setStatus) => {
    const [globalWhitelist, setGlobalWhitelist] = useState([]);
    const [loadingGlobalLists, setLoadingGlobalLists] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [newGlobalWhitelistUrl, setNewGlobalWhitelistUrl] = useState('');

    const whitelistEdit = useInlineEdit();

    const loadGlobalLists = async (signal) => {
        setLoadingGlobalLists(true);
        try {
            const data = await postApi('REACT_APP_URL_GET_GLOBAL_PROXY_LISTS', {}, {
                signal,
                mock: () => {
                    console.log("DEBUG: Caricamento liste globali (MOCK)");
                    setGlobalWhitelist([
                        { id: 'gw1', url: 'example.com', type: 'url' },
                        { id: 'gw2', url: 'google.com', type: 'url' },
                        { id: 'gw3', url: 'github.com', type: 'url' }
                    ]);
                    return null;
                }
            });

            if (data?.data) {
                const whitelistData = (data.data.result.allowed_sites || []).map((item, idx) => ({
                    id: item._id || `gw${idx}`,
                    url: item,
                    type: 'url'
                }));
                setGlobalWhitelist(whitelistData);
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error("Errore nel caricamento delle liste globali:", err);
        } finally {
            setLoadingGlobalLists(false);
        }
    };

    const whitelistHandlers = createGlobalHandlers({
        listSetter: setGlobalWhitelist,
        editHook: whitelistEdit,
        newUrlState: { value: newGlobalWhitelistUrl, set: setNewGlobalWhitelistUrl },
        setStatus,
        setIsSubmitting,
        envKey: 'REACT_APP_URL_UPDATE_GLOBAL_WHITELIST',
        listName: 'whitelist',
        idPrefix: 'gw'
    });

    // Spacchetta il gruppo in URL singoli e li aggiunge tutti alla whitelist globale
    const handleAddGlobalGroup = async (group) => {
        if (!group?.urls?.length) {
            setStatus({ type: 'error', message: 'Il gruppo non contiene URL' });
            return;
        }

        // Filtra URL già presenti nella lista
        let existingUrls;
        setGlobalWhitelist(prev => {
            existingUrls = new Set(prev.map(i => i.url));
            return prev;
        });
        const newUrls = group.urls.filter(u => !existingUrls.has(u));

        if (newUrls.length === 0) {
            setStatus({ type: 'info', message: 'Tutti gli URL del gruppo sono già presenti nella whitelist globale' });
            return;
        }

        setIsSubmitting(true);
        try {
            await postApi('REACT_APP_URL_UPDATE_GLOBAL_WHITELIST', { url: newUrls, action: 'add' }, {
                mock: async () => {
                    await new Promise(r => setTimeout(r, 500));
                    return { statusRequest: 'success' };
                }
            });

            // Aggiunge ogni URL come item singolo
            const newItems = newUrls.map(u => ({ id: genId('gw'), url: u, type: 'url' }));
            setGlobalWhitelist(prev => [...prev, ...newItems]);
            setStatus({ type: 'success', message: `${newItems.length} URL dal gruppo "${group.name}" aggiunti alla whitelist globale` });
        } catch (err) {
            console.error(err);
            setStatus({ type: 'error', message: err.message || 'Errore di connessione' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        // State
        globalWhitelist,
        loadingGlobalLists,
        newGlobalWhitelistUrl,
        // Edit state
        editingGlobalWhitelistId: whitelistEdit.editingId,
        editGlobalWhitelistValue: whitelistEdit.editValue,
        // Setters
        setEditGlobalWhitelistValue: whitelistEdit.setEditValue,
        setNewGlobalWhitelistUrl,
        // Actions
        loadGlobalLists,
        isSubmittingGlobal: isSubmitting,
        handleEditGlobalWhitelist: whitelistHandlers.handleEdit,
        handleSaveGlobalWhitelistEdit: whitelistHandlers.handleSaveEdit,
        handleCancelGlobalWhitelistEdit: whitelistHandlers.handleCancel,
        handleDeleteGlobalWhitelist: whitelistHandlers.handleDelete,
        handleAddNewGlobalWhitelist: whitelistHandlers.handleAdd,
        handleAddGlobalGroup,
    };
};
